import { ReactNode, PropsWithChildren } from "react";

import Box from "@mui/material/Box";
import Grid, { GridProps, GridSize } from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { PropertyOptionType } from "../interfaces";

const DEFAULT_GRID_SIZE = 4;

type ExtendedOptionType = PropertyOptionType & {
  valueLine2?: string;
  gridSize?: GridSize | GridProps;
  inlineButton?: ReactNode;
  showAsTypography?: boolean;
  isHidden?: boolean;
};

interface DisplayPropertiesCardProps {
  properties: ExtendedOptionType[];
  childrenDisplayBlock?: boolean; // if children passed is not a button but instead an unordered list or something else that you don't want on the same line as the label
  smallValues?: boolean;
}

const DisplayPropertiesCard = ({
  properties,
  childrenDisplayBlock,
  smallValues = false,
  children,
}: PropsWithChildren<DisplayPropertiesCardProps>) => {
  return (
    <Box
      border="1px solid"
      borderRadius="10px"
      padding={3}
      borderColor="action.disabled">
      <Grid
        container
        justifyContent={children ? "space-between" : "flex-start"}>
        <Grid
          item
          xs={children && !childrenDisplayBlock ? 10 : 12}
          container
          spacing={4}>
          {properties.map(
            ({
              label,
              value,
              valueLine2,
              gridSize = DEFAULT_GRID_SIZE,
              inlineButton,
              isHidden,
              showAsTypography,
            }) => {
              return isHidden ? null : (
                <Grid
                  item
                  {...(typeof gridSize === "object"
                    ? gridSize
                    : { xs: gridSize })}
                  key={`${label}-${value}`}
                  display="flex"
                  justifyContent="space-between">
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ fontWeight: 300 }}
                      gutterBottom={!value}>
                      {label}
                    </Typography>
                    {["string", "number"].includes(typeof value) ||
                    showAsTypography ? (
                      <Typography
                        sx={{
                          wordBreak: "break-word",
                          fontSize: smallValues ? "18px" : undefined,
                        }}>
                        {value}
                      </Typography>
                    ) : (
                      value
                    )}
                    {valueLine2 && <Typography>{valueLine2}</Typography>}
                  </Box>
                  {inlineButton && <Box alignSelf="center">{inlineButton}</Box>}
                </Grid>
              );
            }
          )}
        </Grid>
        {children && (
          <Grid item display="flex" alignItems="center">
            {children}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DisplayPropertiesCard;
